import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import type { HleSourceSummary } from "@kolectra/shared/schemas";
import { hleApi } from "../lib/api-client.js";
import { PageHead } from "../components/page-head.js";

type SourcePatch = {
  spreadsheetId?: string;
  sheetTab?: string;
  enabled?: boolean;
};

/**
 * Where each language's Item-delivery sheet lives. Sheets are the source of truth
 * for item data — this page only records WHICH sheet, and whether it is in play.
 */
export function HleConfigPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["hle", "sources"],
    queryFn: () => hleApi.sources(),
  });
  const save = useMutation({
    mutationFn: (v: { code: string; patch: SourcePatch }) =>
      hleApi.upsertSource(v.code, v.patch),
    onSuccess: (rows) => qc.setQueryData(["hle", "sources"], rows),
  });

  const rows = q.data ?? [];
  const live = rows.filter((s) => s.enabled && s.spreadsheetId).length;
  const linked = rows.filter((s) => s.spreadsheetId).length;

  return (
    <>
      <PageHead
        crumb="Project · HLE"
        title="HLE languages"
        lead="Point each language at its Item-delivery sheet. Only enabled languages with a sheet can be tested."
      />
      <div className="pad">
        <div className="firewall-note" style={{ marginBottom: 18 }}>
          <div>
            <strong>Two things to do in the sheet itself.</strong> Share it with
            the sync service account so the engine can read and write it, and
            add a <strong>READY_FOR_TEST</strong> column to the Item-delivery
            tab.
          </div>
        </div>

        {q.isLoading && <div className="empty">Loading languages…</div>}
        {q.isError && (
          <div className="banner error">
            Couldn’t load the language list. Reload to try again.
          </div>
        )}
        {save.isError && (
          <div className="banner error" style={{ marginBottom: 12 }}>
            Couldn’t save. {(save.error as Error).message}
          </div>
        )}

        {q.isSuccess && (
          <>
            <div className="runstat" style={{ marginTop: 0, marginBottom: 10 }}>
              <span>{rows.length} languages</span>
              <span>{linked} with a sheet</span>
              <span>{live} enabled for testing</span>
            </div>

            <div className="tablewrap">
              <table className="kolectra">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Language</th>
                    <th style={{ width: "42%" }}>Spreadsheet ID</th>
                    <th style={{ width: "22%" }}>Tab</th>
                    <th style={{ width: 90 }}>Enabled</th>
                    <th style={{ width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <SourceRow
                      key={s.languageCode}
                      source={s}
                      onSave={(patch) =>
                        save.mutate({ code: s.languageCode, patch })
                      }
                      saving={
                        save.isPending &&
                        save.variables?.code === s.languageCode
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function SourceRow({
  source,
  onSave,
  saving,
}: {
  source: HleSourceSummary;
  onSave: (p: SourcePatch) => void;
  saving: boolean;
}) {
  const [sid, setSid] = useState(source.spreadsheetId ?? "");
  const [tab, setTab] = useState(source.sheetTab ?? "");

  const dirty =
    sid !== (source.spreadsheetId ?? "") || tab !== (source.sheetTab ?? "");
  const canEnable = sid.trim().length > 0;

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 500 }}>{source.languageName}</div>
        <div className="mini">{source.languageCode}</div>
      </td>
      <td>
        <input
          className="hle-src-id"
          value={sid}
          onChange={(e) => setSid(e.target.value)}
          placeholder="paste the sheet id from its URL"
          aria-label={`${source.languageName} spreadsheet id`}
        />
      </td>
      <td>
        <input
          className="hle-src-tab"
          value={tab}
          onChange={(e) => setTab(e.target.value)}
          placeholder="Item delivery template"
          aria-label={`${source.languageName} tab name`}
        />
      </td>
      <td>
        <button
          type="button"
          role="switch"
          aria-checked={source.enabled}
          aria-label={`Enable ${source.languageName}`}
          className={`switch ${source.enabled ? "on" : ""}`}
          disabled={!canEnable && !source.enabled}
          title={
            canEnable || source.enabled
              ? undefined
              : "Add a spreadsheet id first"
          }
          onClick={() => onSave({ enabled: !source.enabled })}
        >
          <i />
        </button>
      </td>
      <td>
        {/* Nothing to show for a row that was never configured — an idle "Saved" there
            would claim something happened. */}
        {dirty || saving ? (
          <button
            className="btn sm"
            disabled={saving}
            onClick={() =>
              onSave({ spreadsheetId: sid.trim(), sheetTab: tab.trim() })
            }
          >
            {saving ? <Loader2 className="spin" /> : null}
            {saving ? "Saving" : "Save"}
          </button>
        ) : source.spreadsheetId ? (
          <span
            className="mini"
            style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <Check style={{ width: 13, height: 13, color: "var(--good)" }} />
            Saved
          </span>
        ) : null}
      </td>
    </tr>
  );
}
